import React, { useState } from 'react';
import { Check, ArrowRight } from 'lucide-react';
import Reveal from '@/components/Reveal';
import { CONTACT } from '@/data/contact';
import CurrencyToggle from '@/components/CurrencyToggle';
import { CURRENCIES, convertPrice } from '@/data/currencies';

export default function ServicePricing({ tiers = [], note, serviceTitle, contactEmail = CONTACT.bookings }) {
  const [code, setCode] = useState('USD');
  const currency = CURRENCIES.find((c) => c.code === code) || CURRENCIES[0];

  return (
    <section className="bg-[#0B0B0B] border-t border-white/10 py-20 lg:py-28">
      <div className="max-w-[1320px] mx-auto px-6 lg:px-10">
        <Reveal className="max-w-2xl mb-14">
          <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#3B82F6] block mb-4">
            Engagement Model
          </span>
          <h2 className="font-display font-bold text-white tracking-tight text-3xl lg:text-4xl leading-tight">
            How we structure commercial terms
          </h2>
          <p className="mt-6 text-white/55 font-light leading-relaxed">
            Every engagement is contracted in advance with defined scope, usage rights and reporting
            obligations. The structures below are our standard commercial frameworks; final terms are
            set against category, market and exclusivity requirements.
          </p>
        </Reveal>

        <Reveal delay={0.05} className="mb-8 flex flex-wrap items-center gap-4">
          <CurrencyToggle value={code} onChange={setCode} />
          <span className="text-[11px] uppercase tracking-[0.16em] text-white/35">
            {code === 'USD' ? 'Quoted in US dollars' : 'Indicative conversion from USD'}
          </span>
        </Reveal>

        <div className="grid md:grid-cols-3 gap-6">
          {tiers.map((t, i) => (
            <Reveal key={t.name} delay={i * 0.1}>
              <div
                className={`h-full flex flex-col rounded-lg p-8 lg:p-9 transition-all duration-500 ${
                  t.featured
                    ? 'border border-[#3B82F6]/60 bg-[#2563EB]/[0.07] blue-glow'
                    : 'border border-white/10 bg-white/[0.03] hover:border-white/25'
                }`}
              >
                <div className="flex items-center justify-between gap-3 mb-6">
                  <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#3B82F6]">
                    {t.structure}
                  </span>
                  {t.featured && (
                    <span className="text-[9px] font-semibold uppercase tracking-[0.16em] px-2.5 py-1 rounded-full bg-[#2563EB] text-white">
                      Most Selected
                    </span>
                  )}
                </div>

                <h3 className="font-display font-bold text-2xl text-white tracking-tight">{t.name}</h3>
                <p className="mt-3 text-sm text-white/55 font-light leading-relaxed">{t.summary}</p>

                <div className="mt-7 pt-7 border-t border-white/10">
                  <div className="font-display font-bold text-3xl text-white">{convertPrice(t.from, currency)}</div>
                  <div className="text-[11px] uppercase tracking-[0.16em] text-white/40 mt-1.5">{t.basis}</div>
                </div>

                <ul className="mt-7 space-y-3.5 flex-1">
                  {t.includes.map((inc) => (
                    <li key={inc} className="flex gap-3 items-start">
                      <Check className="w-3.5 h-3.5 text-[#3B82F6] mt-1 shrink-0" />
                      <span className="text-sm text-white/60 font-light leading-relaxed">{inc}</span>
                    </li>
                  ))}
                </ul>

                <a
                  href={`mailto:${contactEmail}?subject=${encodeURIComponent(
                    `${serviceTitle} — ${t.name} enquiry`
                  )}`}
                  className={`group mt-9 inline-flex items-center justify-center gap-2 h-12 rounded-lg text-sm font-semibold transition-colors ${
                    t.featured
                      ? 'bg-[#2563EB] text-white hover:bg-[#1D4ED8]'
                      : 'border border-white/20 text-white hover:border-[#3B82F6]'
                  }`}
                >
                  Request Terms
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </a>
              </div>
            </Reveal>
          ))}
        </div>

        {note && (
          <Reveal delay={0.2}>
            <p className="mt-10 text-xs text-white/35 font-light leading-relaxed max-w-3xl">{note}</p>
          </Reveal>
        )}
      </div>
    </section>
  );
}